import { CommonModule, DatePipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { HttpErrorResponse } from "@angular/common/http";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subject, forkJoin, merge, of } from "rxjs";
import {
  catchError,
  debounceTime,
  map,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { DocumentType } from "../../documents";
import {
  DocumentDetailsRecord,
  getTemplateForLanguage,
  upsertTemplateForLanguage,
  DocumentTemplate,
} from "../../document-details.model";
import { DocumentDetailsService } from "../../document-details.service";
import { LanguageService } from "app/language/language.service";
import { OrderService } from "app/orders/order.service";
import { DocumentService } from "../../document.service";
import { Document } from "../../documents";
import {
  SALES_AGREEMENT_VARIABLES,
  SalesAgreementVariable,
} from "../sales-agreement-variables.constants";

@Component({
  selector: "app-sales-agreement-document",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatDividerModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatDialogModule,
  ],
  providers: [DatePipe],
  templateUrl: "./sales-agreement-document.component.html",
  styleUrls: ["./sales-agreement-document.component.css"],
})
export class SalesAgreementDocumentComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  /** Eksponowane stałe zmiennych dla szablonu HTML */
  readonly variables: SalesAgreementVariable[] = SALES_AGREEMENT_VARIABLES;
  readonly requiredTranslationVariables: SalesAgreementVariable[] =
    SALES_AGREEMENT_VARIABLES.filter(
      (variable) => variable.requiredTranslation,
    );
  readonly buyerVariables: SalesAgreementVariable[] =
    SALES_AGREEMENT_VARIABLES.filter((variable) => variable.group === "buyer");
  readonly sellerVariables: SalesAgreementVariable[] =
    SALES_AGREEMENT_VARIABLES.filter((variable) => variable.group === "seller");
  readonly otherVariables: SalesAgreementVariable[] =
    SALES_AGREEMENT_VARIABLES.filter((variable) => variable.group === "other");

  /** Ścieżka do grafiki podpisu zastępującej {{podpis}} przy druku */
  readonly SIGNATURE_IMAGE_PATH =
    "assets/images/sales-agreement-document/podpis-michal.png";
  /** Grafika nagłówka druku – logo firmy wyrównane do lewej, maks. 90px wysokości */
  readonly PRINT_HEADER_IMAGE_PATH = "assets/images/logo.svg";
  /** Globalna wysokość nagłówka druku (w mm) */
  readonly PRINT_HEADER_HEIGHT_MM = 26;
  /** Maksymalna wysokość logo w nagłówku (w mm) */
  readonly PRINT_HEADER_LOGO_MAX_HEIGHT_MM = 14;
  /** Globalna wysokość stopki druku (w mm) */
  readonly PRINT_FOOTER_HEIGHT_MM = 20;
  /** Maksymalna liczba linii treści stopki */
  readonly PRINT_FOOTER_MAX_LINES = 4;
  /** Dodatkowy bezpieczny odstęp między treścią a nagłówkiem/stopką (w mm) */
  readonly PRINT_CONTENT_SAFE_GAP_MM = 2;
  /**
   * Szablony nagłówka i stopki wydruku dla każdej spółki.
   * Klucz odpowiada wartości z selectOptions sellerPartnership.
   */
  private readonly PARTNERSHIP_PRINT_TEMPLATES: Record<
    string,
    { headerContactHtml: string; footerHtml: string; krsNip: string }
  > = {
    "Auto-CUBY Spółka z ograniczoną odpowiedzialnością": {
      headerContactHtml: `
        <p>Auto-CUBY Sp. z o.o.</p>
        <p>KRS: 0000382450 | REGON: 221203580</p>
        <p>NIP: 588 237 59 04</p>
        <p>ul. Przemysłowa 2, 84-241 Gościcino</p>
      `,
      footerHtml: `
        <p>Auto-CUBY Sp. z o.o., ul. Przemysłowa 2, 84-241 Gościcino</p>
        <p>NIP: 588 237 59 04 &nbsp;|&nbsp; KRS: 0000382450 &nbsp;|&nbsp; Regon: 221203580</p>
        <p>tel. +48 509 388 082 &nbsp;|&nbsp; sekretariat@autocuby.pl &nbsp;|&nbsp; www.autocuby.eu</p>
      `,
      krsNip: "KRS: 0000382450 NIP: 5882375904",
    },
    "Auto-CUBY Sp. z o.o. Spółka Komandytowa": {
      headerContactHtml: `
        <p>Auto-CUBY Sp. z o.o. Spółka Komandytowa</p>
        <p>KRS: 0000452729 | REGON: 221840223</p>
        <p>NIP: 5882399098</p>
        <p>ul. Przemysłowa 2, 84-241 Gościcino</p>
      `,
      footerHtml: `
        <p>Auto-CUBY Sp. z o.o. Spółka Komandytowa, ul. Przemysłowa 2, 84-241 Gościcino</p>
        <p>NIP: 588 239 90 98 &nbsp;|&nbsp; KRS: 0000452729</p>
        <p>tel. +48 509 388 082 &nbsp;|&nbsp; sekretariat@autocuby.pl &nbsp;|&nbsp; www.autocuby.eu</p>
      `,
      krsNip: "KRS: 0000452729 NIP: 5882399098",
    },
    "M&M Cuby Bus Sp. z o.o.": {
      headerContactHtml: `
        <p>M&M Cuby Bus Sp. z o.o.</p>
        <p>KRS: 0000817793 | REGON: 385050910</p>
        <p>NIP: 5882455052</p>
        <p>ul. Przemysłowa 2, 84-241 Gościcino</p>
      `,
      footerHtml: `
        <p>M&M Cuby Bus Sp. z o.o., ul. Przemysłowa 2, 84-241 Gościcino</p>
        <p>NIP: 588 245 50 52 &nbsp;|&nbsp; KRS: 0000817793</p>
        <p>tel. +48 509 388 082 &nbsp;|&nbsp; sekretariat@autocuby.pl &nbsp;|&nbsp; www.autocuby.eu</p>
      `,
      krsNip: "KRS: 0000817793 NIP: 5882455052",
    },
  };

  /**
   * Zawartość teleadresowa wyświetlana po prawej stronie nagłówka (4 wiersze)
   * Zwraca szablon na podstawie wybranej spółki (sellerPartnership).
   */
  get PRINT_HEADER_CONTACT_HTML(): string {
    const sellerPartnership = this.form?.get("sellerPartnership")?.value || "";
    return (
      this.PARTNERSHIP_PRINT_TEMPLATES[sellerPartnership]?.headerContactHtml ||
      this.PARTNERSHIP_PRINT_TEMPLATES["Auto-CUBY Sp. z o.o."].headerContactHtml
    );
  }

  /**
   * Zawartość HTML stopki wydruku.
   * Zwraca szablon na podstawie wybranej spółki (sellerPartnership).
   */
  get PRINT_FOOTER_HTML(): string {
    const sellerPartnership = this.form?.get("sellerPartnership")?.value || "";
    return (
      this.PARTNERSHIP_PRINT_TEMPLATES[sellerPartnership]?.footerHtml ||
      this.PARTNERSHIP_PRINT_TEMPLATES["Auto-CUBY Sp. z o.o."].footerHtml
    );
  }

  form: FormGroup;
  orderId: string | null = null;
  order: any = null;
  languages: any[] = [];

  isLoadingOrder = false;
  isLoadingDocument = false;
  isSaving = false;
  isResetting = false;
  /** Czy dokument został zapisany i nie było późniejszych zmian – warunkuje widoczność przycisku Drukuj */
  canPrint = false;
  /** Czy użytkownik wprowadził zmiany w polach zmiennych od ostatniego zapisu */
  hasUnsavedChanges = false;
  /** Rola zalogowanego użytkownika (do sprawdzenia uprawnień superadmin) */
  userRole: string | null = null;
  /** Kody uprawnień zalogowanego użytkownika (do odblokowania edycji wybranych pól) */
  permissions: string[] = [];

  /** Surowy wyrenderowany HTML (do druku i zapisu) */
  private rawRenderedHtml: string | null = null;

  /** Bezpieczny HTML podglądu wyświetlany w iframe pod formularzem */
  previewHtml: SafeHtml | null = null;
  /** Czy sekcja iframe podglądu jest widoczna */
  isPreviewVisible = false;

  /** Ostatnio pobrany szablon umowy (do ponownego użycia przy zapisie bez nowego podglądu) */
  private currentAgreement: Document | null = null;

  /**
   * Pamięć robocza wartości pól per język (na potrzeby przełączania języków bez utraty danych).
   * @since 17.04.2026
   */
  private draftValuesByLanguage: Record<string, Record<string, any>> = {};
  private persistedOrderDerivedValuesByLanguage: Record<
    string,
    Record<string, any>
  > = {};
  private activeLanguage: string | null = null;
  private requiredTranslationsByLanguage: Record<
    string,
    Record<string, Record<string, any>>
  > = {};
  private activeSecondaryLanguage: string | null = null;
  private readonly REQUIRED_TRANSLATIONS_META_KEY = "translations";
  /** Templates z załadowanego dokumentu - używane do walidacji trybu dwujęzycznego */
  private currentDocumentTemplates: DocumentTemplate[] | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private orderService: OrderService,
    private languageService: LanguageService,
    private documentService: DocumentService,
    private sanitizer: DomSanitizer,
    private documentDetailsService: DocumentDetailsService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
  ) {
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.loadLanguages();
    this.userRole = localStorage.getItem("role");
    this.permissions = JSON.parse(
      localStorage.getItem("specificatorPermissions") || "[]",
    );
    this.applyVariableControlStates();
    this.orderId = this.route.snapshot.queryParamMap.get("orderId");

    if (this.orderId) {
      this.loadOrder(this.orderId);
    }

    this.form.get("language")?.valueChanges.subscribe((language: string) => {
      if (
        this.hasUnsavedChanges &&
        this.activeLanguage &&
        language !== this.activeLanguage
      ) {
        this.form.patchValue(
          { language: this.activeLanguage },
          { emitEvent: false },
        );
        return;
      }
      this.onLanguageChanged(language);
    });

    // Live-update podglądu iframe + reset canPrint tylko przy zmianie pól zmiennych
    const variableControls$ = SALES_AGREEMENT_VARIABLES.map(
      (v) => this.form.get(this.toControlName(v.key))?.valueChanges,
    ).filter((obs): obs is Observable<any> => !!obs);

    const requiredTranslationControls$ = this.requiredTranslationVariables
      .map(
        (v) =>
          this.form.get(this.toRequiredTranslationControlName(v.key))
            ?.valueChanges,
      )
      .filter((obs): obs is Observable<any> => !!obs);

    merge(...variableControls$, ...requiredTranslationControls$)
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.persistSecondaryTranslationDraft();
        this.hasUnsavedChanges = true;
        this.canPrint = false;
        if (this.isPreviewVisible && this.currentAgreement) {
          this.refreshPreview();
        }
      });

    this.form
      .get("secondaryLanguage")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((secondaryLanguage: string) => {
        this.persistSecondaryTranslationDraft();
        this.removeSecondaryTranslationControls();

        if (!secondaryLanguage) {
          this.activeSecondaryLanguage = null;
          // Sprawdź czy można drukować po zmianie języka drugorzędnego
          this.validateDualLanguagePrintability();
          return;
        }

        this.restoreSecondaryTranslationDraft(secondaryLanguage);
        // Sprawdź czy można drukować po zmianie języka drugorzędnego
        this.validateDualLanguagePrintability();
      });

    this.form
      .get("includeDualLanguage")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        if (!enabled) {
          this.persistSecondaryTranslationDraft();
          this.form.patchValue({ secondaryLanguage: "" }, { emitEvent: false });
          this.activeSecondaryLanguage = null;
          this.removeSecondaryTranslationControls();
          return;
        }

        const primaryLanguage = this.getPrimaryLanguageKey();
        let secondaryLanguage = this.getSelectedSecondaryLanguageKey();
        if (
          !secondaryLanguage ||
          (primaryLanguage && secondaryLanguage === primaryLanguage)
        ) {
          secondaryLanguage =
            this.languages.find((lang) => lang?.name !== primaryLanguage)
              ?.name || "";
          this.form.patchValue(
            { secondaryLanguage },
            {
              emitEvent: false,
            },
          );
        }

        if (secondaryLanguage && secondaryLanguage !== primaryLanguage) {
          this.restoreSecondaryTranslationDraft(secondaryLanguage);
        }

        // Sprawdź czy można drukować po włączeniu trybu dwujęzycznego
        this.validateDualLanguagePrintability();
      });

    // Aktualizuj sellerKrsNip przy zmianie sellerPartnership
    this.form
      .get("sellerPartnership")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const krsNipValue = this.computeVariableValue(null, "{{sellerKrsNip}}");
        this.form.patchValue(
          { sellerKrsNip: krsNipValue },
          { emitEvent: false },
        );
        // Odśwież podgląd jeśli widoczny
        if (this.isPreviewVisible && this.currentAgreement) {
          this.refreshPreview();
        }
      });
  }

  /**
   * Funkcja odpowiedzialna za budowanie reaktywnego formularza z kontrolkami
   * dla każdej zmiennej szablonu i pól wyboru języka.
   * Kontrolki dla tłumaczeń będą dodawane/usuwane dynamicznie.
   * @since 17.04.2026
   */
  private buildForm(): FormGroup {
    const controls: Record<string, any> = {
      language: ["", Validators.required],
      secondaryLanguage: [""],
      includeDualLanguage: [false],
    };
    SALES_AGREEMENT_VARIABLES.forEach((v) => {
      controls[this.toControlName(v.key)] = [
        { value: "", disabled: this.isVariableDisabled(v) },
      ];
    });
    // NIE dodajemy kontrolek dla tłumaczeń - będą dynamicznie tworzone
    return this.fb.group(controls);
  }

  /**
   * Dla superadmin (lub użytkownika z uprawnieniem editPermission) pola
   * technicznie oznaczone jako disabled są edytowalne.
   */
  isVariableDisabled(variable: SalesAgreementVariable): boolean {
    return !!variable.disabled && !this.canEditVariable(variable);
  }

  /**
   * Dla superadmin (lub użytkownika z uprawnieniem editPermission): pole orderDerived
   * uznajemy za nadpisane, jeżeli jest już zapisane w rekordzie lub zostało ręcznie zmodyfikowane.
   */
  isOrderDerivedOverridden(variable: SalesAgreementVariable): boolean {
    if (!this.canEditVariable(variable) || !variable.orderDerived) {
      return false;
    }

    const controlName = this.toControlName(variable.key);
    const language = this.getPrimaryLanguageKey();
    const persistedOrderDerived = language
      ? this.persistedOrderDerivedValuesByLanguage[language] || {}
      : {};

    const hasPersistedValue = Object.prototype.hasOwnProperty.call(
      persistedOrderDerived,
      controlName,
    );
    const wasEdited = !!this.form.get(controlName)?.dirty;

    return hasPersistedValue || wasEdited;
  }

  private isSuperadmin(): boolean {
    return this.userRole === "superadmin";
  }

  /**
   * Funkcja odpowiedzialna za sprawdzenie, czy użytkownik może edytować zmienną
   * (superadmin lub posiadacz uprawnienia z editPermission).
   */
  private canEditVariable(variable: SalesAgreementVariable): boolean {
    return (
      this.isSuperadmin() ||
      (!!variable.editPermission &&
        this.permissions.includes(variable.editPermission))
    );
  }

  /**
   * Synchronizuje stan enable/disable kontrolek po ustaleniu roli użytkownika.
   */
  private applyVariableControlStates(): void {
    SALES_AGREEMENT_VARIABLES.forEach((variable) => {
      const control = this.form.get(this.toControlName(variable.key));
      if (!control) {
        return;
      }

      if (this.isVariableDisabled(variable)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  /**
   * Funkcja odpowiedzialna za pobranie listy dostępnych języków
   * do wyboru szablonu umowy sprzedaży.
   * @since 17.04.2026
   */
  private loadLanguages(): void {
    this.languageService.getLanguages().subscribe((data: any) => {
      this.languages = data;
    });
  }

  /**
   * Funkcja odpowiedzialna za pobranie danych zamówienia i wstępne
   * wypełnienie pól formularza wartościami z zamówienia.
   * @since 17.04.2026
   * @param id Identyfikator zamówienia MongoDB
   */
  private loadOrder(id: string): void {
    this.isLoadingOrder = true;
    this.orderService.getSingleOrder(id).subscribe({
      next: (order: any) => {
        this.order = order;
        this.autoFillFromOrder(order);

        if (!this.form.get("language")?.value) {
          this.form.patchValue({ language: "PL" });
        }

        this.isLoadingOrder = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error("Błąd pobierania zamówienia:", err);
        this.isLoadingOrder = false;
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za automatyczne uzupełnienie pól formularza
   * wartościami pobranymi z obiektu zamówienia.
   * @since 17.04.2026
   * @param order Obiekt zamówienia zwrócony przez API
   */
  private autoFillFromOrder(order: any): void {
    const patch: Record<string, any> = {};
    SALES_AGREEMENT_VARIABLES.forEach((v) => {
      if (!v.orderPath && v.value === undefined) {
        return;
      }
      const value = this.resolveVariableValue(order, v);
      if (value !== null && value !== undefined && value !== "") {
        patch[this.toControlName(v.key)] = value;
      }
    });
    this.form.patchValue(patch, { emitEvent: false });
  }

  /**
   * Funkcja odpowiedzialna za obsługę przełączenia języka dokumentu.
   * @since 17.04.2026
   */
  private onLanguageChanged(language: string): void {
    if (!language) {
      return;
    }

    this.persistSecondaryTranslationDraft();

    if (this.activeLanguage) {
      this.draftValuesByLanguage[this.activeLanguage] =
        this.getCurrentVariableValues();
    }

    this.restoreOrInitializeLanguageValues(language);
    this.activeLanguage = language;

    const existingDraft = this.draftValuesByLanguage[language];
    if (existingDraft) {
      this.loadRequiredTranslationsFromFieldValues(language, existingDraft);
    }

    this.loadSavedDocumentForLanguage(language);
  }

  /**
   * Funkcja odpowiedzialna za odtworzenie wartości pól dla wybranego języka
   * lub inicjalizację nowych pól na podstawie danych zamówienia.
   * @since 17.04.2026
   */
  private restoreOrInitializeLanguageValues(language: string): void {
    const patch: Record<string, any> = {};

    const existingDraft = this.draftValuesByLanguage[language];
    if (existingDraft) {
      this.form.patchValue(existingDraft, { emitEvent: false });
      const primaryLanguage = this.getPrimaryLanguageKey();
      const secondaryLanguage = this.getSelectedSecondaryLanguageKey();
      if (
        this.form.get("includeDualLanguage")?.value &&
        secondaryLanguage &&
        secondaryLanguage !== primaryLanguage
      ) {
        this.restoreSecondaryTranslationDraft(secondaryLanguage);
      }
      return;
    }

    const previousLanguageDraft =
      this.activeLanguage && this.draftValuesByLanguage[this.activeLanguage]
        ? this.draftValuesByLanguage[this.activeLanguage]
        : null;

    SALES_AGREEMENT_VARIABLES.forEach((variable) => {
      const controlName = this.toControlName(variable.key);

      if (
        !variable.flexible &&
        previousLanguageDraft?.[controlName] !== undefined
      ) {
        patch[controlName] = previousLanguageDraft[controlName];
        return;
      }

      if (this.order) {
        patch[controlName] = this.resolveVariableValue(this.order, variable);
      }
    });

    const currentSecondaryLanguage = this.getSelectedSecondaryLanguageKey();
    if (!currentSecondaryLanguage || currentSecondaryLanguage === language) {
      patch.secondaryLanguage =
        this.languages.find((lang) => lang?.name && lang.name !== language)
          ?.name || "";
    }

    this.form.patchValue(patch, { emitEvent: false });

    const selectedSecondaryLanguage = this.getSelectedSecondaryLanguageKey();
    const primaryLanguage = this.getPrimaryLanguageKey();
    if (
      this.form.get("includeDualLanguage")?.value &&
      selectedSecondaryLanguage &&
      selectedSecondaryLanguage !== primaryLanguage
    ) {
      this.restoreSecondaryTranslationDraft(selectedSecondaryLanguage);
    }
  }

  /**
   * Funkcja odpowiedzialna za pobranie zapisanego rekordu dokumentu dla danego języka.
   * @since 17.04.2026
   */
  private loadSavedDocumentForLanguage(language: string): void {
    this.canPrint = false;
    if (!this.orderId) {
      return;
    }

    this.isLoadingDocument = true;

    this.documentDetailsService
      .getSingleDocumentDetails({
        orderId: this.orderId,
        type: DocumentType.SALES_AGREEMENT,
      })
      .subscribe({
        next: (record: DocumentDetailsRecord) => {
          // Zapisz templates do walidacji trybu dwujęzycznego
          this.currentDocumentTemplates = record?.templates || null;

          const template = getTemplateForLanguage(record?.templates, language);
          if (template?.documentTemplate) {
            this.currentAgreement = {
              _id: record.documentId,
              language: language,
              type: record.type,
              content: template.documentTemplate,
            };
            this.canPrint = true;

            // Sprawdź czy szablon dla drugiego języka istnieje (jeśli tryb dwujęzyczny jest włączony)
            this.validateDualLanguagePrintability();
          }
          this.rawRenderedHtml = template?.document || null;

          const persistedOrderDerivedValues = this.pickOrderDerivedFieldValues(
            record?.fieldValues || {},
          );
          this.persistedOrderDerivedValuesByLanguage[language] =
            persistedOrderDerivedValues;

          const loadedFieldValues = record?.fieldValues || {};

          this.form.patchValue(loadedFieldValues, { emitEvent: false });
          this.loadRequiredTranslationsFromFieldValues(
            language,
            loadedFieldValues,
          );
          this.draftValuesByLanguage[language] = {
            ...loadedFieldValues,
          };

          const secondaryLanguage = this.getSelectedSecondaryLanguageKey();
          if (
            this.form.get("includeDualLanguage")?.value &&
            secondaryLanguage &&
            secondaryLanguage !== language
          ) {
            this.restoreSecondaryTranslationDraft(secondaryLanguage);
          }

          this.isLoadingDocument = false;
        },
        error: (error: HttpErrorResponse) => {
          if (error.status !== 404) {
            console.error("Błąd pobierania dokumentu umowy:", error);
          } else {
            this.persistedOrderDerivedValuesByLanguage[language] = {};
            this.currentDocumentTemplates = null;
          }
          this.isLoadingDocument = false;
        },
      });
  }

  /**
   * Funkcja walidująca czy szablon dla drugiego języka istnieje w trybie dwujęzycznym.
   * Jeśli nie - ustawia canPrint = false.
   * @since 13.07.2026
   */
  private validateDualLanguagePrintability(): void {
    const includeDualLanguage = this.form.get("includeDualLanguage")?.value;
    if (!includeDualLanguage) {
      return; // Nie ma trybu dwujęzycznego, nie trzeba walidować
    }

    const secondaryLanguage = this.getSelectedSecondaryLanguageKey();
    if (!secondaryLanguage) {
      this.canPrint = false;
      return;
    }

    // Sprawdź czy szablon dla drugiego języka istnieje
    const secondaryTemplate = getTemplateForLanguage(
      this.currentDocumentTemplates || undefined,
      secondaryLanguage,
    );

    if (!secondaryTemplate?.documentTemplate) {
      this.canPrint = false;
      console.warn(
        `Brak szablonu dla języka ${secondaryLanguage}. Drukowanie wyłączone.`,
      );
    }
  }

  /**
   * Zwraca wartość zmiennej szablonu na podstawie pola zamówienia lub logiki obliczeniowej.
   * Obsługuje typy formatowania: 'date', 'currency', 'computed' oraz domyślny 'text'.
   * @since 17.04.2026
   * @param order Obiekt zamówienia
   * @param variable Definicja zmiennej szablonu
   */
  private resolveVariableValue(
    order: any,
    variable: SalesAgreementVariable,
  ): any {
    if (variable.formatter === "computed") {
      return this.computeVariableValue(order, variable.key);
    }

    if (variable.value !== undefined) {
      return variable.value;
    }

    if (!variable.orderPath) {
      return "";
    }

    const raw = this.getNestedValue(order, variable.orderPath!);

    if (variable.formatter === "date" && raw) {
      return this.datePipe.transform(raw, "yyyy-MM-dd") || raw;
    }

    if (variable.formatter === "currency" && raw !== undefined) {
      return raw;
    }

    return raw ?? "";
  }

  /**
   * Funkcja odpowiedzialna za obliczenie wartości zmiennych wymagających dedykowanej logiki biznesowej.
   * @since 17.04.2026
   */
  private computeVariableValue(order: any, key: string): any {
    switch (key) {
      case "{{priceNetto}}": {
        const priceNetto = Number(order?.finances?.prices?.priceNetto) || 0;
        const discountNetto =
          Number(order?.finances?.prices?.discountNetto) || 0;
        return priceNetto - discountNetto;
      }
      case "{{advancePayments}}": {
        const payments: any[] = order?.finances?.advancePayments || [];
        const totalNetAdvancePayments = payments.reduce(
          (acc: number, p: any) => acc + (Number(p?.value) || 0),
          0,
        );
        return totalNetAdvancePayments;
      }
      case "{{advancePaymentsBrutto}}": {
        const payments: any[] = order?.finances?.advancePayments || [];
        const totalNetAdvancePayments = payments.reduce(
          (acc: number, p: any) => acc + (Number(p?.value) || 0),
          0,
        );
        const totalBruttoAdvancePaymens = (
          totalNetAdvancePayments * 1.23
        ).toFixed(2);
        return totalBruttoAdvancePaymens;
      }
      case "{{orderManager}}": {
        const firstName = order?.creator?.firstName || "";
        const lastName = order?.creator?.lastName || "";
        return [firstName, lastName].filter(Boolean).join(" ");
      }
      case "{{vehicleData}}": {
        const variantLabel =
          order?.places?.variants?.[0]?.variantName ?? order?.placesCount;

        const parts = [
          order?.producent?.name,
          order?.producent?.type,
          order?.conversion?.name,
          order?.busModel,
          variantLabel,
        ].filter(Boolean);

        return parts.join(" ");
      }
      case "{{specificationNumber}}": {
        const countBeforeHardening = order?.orderTitle?.countBeforeHardening;
        const count =
          countBeforeHardening !== undefined &&
          countBeforeHardening !== null &&
          countBeforeHardening !== ""
            ? countBeforeHardening
            : order?.orderTitle?.count;
        const year = order?.orderTitle?.year;
        if (count && year) {
          return `${count}/${year}`;
        }
        return count ?? "";
      }
      case "{{sellerKrsNip}}": {
        const sellerPartnership =
          this.form?.get("sellerPartnership")?.value || "";
        return (
          this.PARTNERSHIP_PRINT_TEMPLATES[sellerPartnership]?.krsNip ||
          this.PARTNERSHIP_PRINT_TEMPLATES[
            "Auto-CUBY Spółka z ograniczoną odpowiedzialnością"
          ].krsNip
        );
      }
      default:
        return "";
    }
  }

  /**
   * Funkcja odpowiedzialna za formatowanie kwoty numerycznej jako ciąg z walutą.
   * @since 17.04.2026
   */
  private formatCurrency(value: number | string, currency: string): string {
    return `${Number(value).toLocaleString("pl-PL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  }

  /**
   * Funkcja odpowiedzialna za pobranie zagnieżdżonej wartości obiektu na podstawie ścieżki dot-notation.
   * @since 17.04.2026
   */
  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) {
      return "";
    }
    return path.split(".").reduce((acc: any, key: string) => acc?.[key], obj);
  }

  /**
   * Funkcja odpowiedzialna za konwersję klucza szablonu na nazwę kontrolki formularza.
   * @since 17.04.2026
   */
  toControlName(key: string): string {
    return key.replace("{{", "").replace("}}", "");
  }

  /**
   * Funkcja odpowiedzialna za zwrócenie opcji wyboru dla zmiennej szablonu.
   * @since 13.07.2026
   */
  getSelectOptions(
    variable: SalesAgreementVariable,
  ): Array<{ value: string; label: string }> {
    if (variable.selectOptions?.length) {
      return variable.selectOptions;
    }

    return [
      { value: "EUR", label: "EUR" },
      { value: "PLN", label: "PLN" },
    ];
  }

  /**
   * Funkcja odpowiedzialna za zwrócenie etykiety języka drugorzędnego.
   * @since 13.07.2026
   */
  getSecondaryLanguageLabel(): string {
    return this.getSelectedSecondaryLanguageKey();
  }

  /**
   * Funkcja odpowiedzialna za konwersję klucza szablonu na nazwę kontrolki tłumaczenia.
   * @since 13.07.2026
   */
  toRequiredTranslationControlName(key: string): string {
    return `translation_${this.toControlName(key)}`;
  }

  /**
   * Zapisuje bieżące wartości kontrolek tłumaczeń do pamięci podręcznej.
   * Używane przy przełączaniu języków, aby nie utracić wprowadzonych tłumaczeń.
   * @since 17.04.2026
   */
  private persistSecondaryTranslationDraft(): void {
    const primaryLanguage = this.getPrimaryLanguageKey();
    const secondaryLanguage =
      this.activeSecondaryLanguage || this.getSelectedSecondaryLanguageKey();

    if (
      !primaryLanguage ||
      !secondaryLanguage ||
      secondaryLanguage === primaryLanguage
    ) {
      return;
    }

    if (!this.requiredTranslationsByLanguage[primaryLanguage]) {
      this.requiredTranslationsByLanguage[primaryLanguage] = {};
    }

    this.requiredTranslationsByLanguage[primaryLanguage][secondaryLanguage] =
      this.collectSecondaryTranslationControls();
    this.activeSecondaryLanguage = secondaryLanguage;
  }

  /**
   * Przywraca z pamięci podręcznej wartości tłumaczeń dla wybranego języka drugorzędnego.
   * Jeśli nie ma zapisanych wartości, inicjalizuje je wartościami z języka głównego.
   * @param secondaryLanguage Kod języka drugorzędnego (np. 'EN', 'DE')
   * @since 17.04.2026
   */
  private restoreSecondaryTranslationDraft(secondaryLanguage: string): void {
    const primaryLanguage = this.getPrimaryLanguageKey();
    if (
      !primaryLanguage ||
      !secondaryLanguage ||
      secondaryLanguage === primaryLanguage
    ) {
      return;
    }

    if (!this.requiredTranslationsByLanguage[primaryLanguage]) {
      this.requiredTranslationsByLanguage[primaryLanguage] = {};
    }

    const existingDraft =
      this.requiredTranslationsByLanguage[primaryLanguage][secondaryLanguage];

    if (existingDraft) {
      this.addSecondaryTranslationControls(existingDraft);
      this.patchSecondaryTranslationControls(existingDraft);
      this.activeSecondaryLanguage = secondaryLanguage;
      return;
    }

    const initializedValues: Record<string, any> = {};
    this.requiredTranslationVariables.forEach((variable) => {
      const baseControl = this.toControlName(variable.key);
      initializedValues[baseControl] = this.form.get(baseControl)?.value ?? "";
    });

    this.addSecondaryTranslationControls(initializedValues);
    this.requiredTranslationsByLanguage[primaryLanguage][secondaryLanguage] = {
      ...initializedValues,
    };
    this.patchSecondaryTranslationControls(initializedValues);
    this.activeSecondaryLanguage = secondaryLanguage;
  }

  /**
   * Funkcja odpowiedzialna za zebranie wartości z kontrolek tłumaczeń drugorzędnych.
   * @since 13.07.2026
   */
  private collectSecondaryTranslationControls(): Record<string, any> {
    const values: Record<string, any> = {};

    this.requiredTranslationVariables.forEach((variable) => {
      const valueKey = this.toControlName(variable.key);
      const translationControl = this.toRequiredTranslationControlName(
        variable.key,
      );
      values[valueKey] = this.form.get(translationControl)?.value ?? "";
    });

    return values;
  }

  /**
   * Funkcja odpowiedzialna za usuwanie kontrolek tłumaczeń drugorzędnych z formularza.
   * @since 13.07.2026
   */
  private removeSecondaryTranslationControls(): void {
    this.requiredTranslationVariables.forEach((variable) => {
      const controlName = this.toRequiredTranslationControlName(variable.key);
      if (this.form.get(controlName)) {
        this.form.removeControl(controlName);
      }
    });
  }

  /**
   * Funkcja odpowiedzialna za dodawanie kontrolek tłumaczeń drugorzędnych do formularza.
   * Subskrybuje valueChanges dla każdej nowo dodanej kontrolki, aby reagować na zmiany.
   * @since 13.07.2026
   */
  private addSecondaryTranslationControls(
    values: Record<string, any> = {},
  ): void {
    this.requiredTranslationVariables.forEach((variable) => {
      const valueKey = this.toControlName(variable.key);
      const controlName = this.toRequiredTranslationControlName(variable.key);
      if (!this.form.get(controlName)) {
        this.form.addControl(
          controlName,
          this.fb.control(values[valueKey] ?? ""),
        );
        // Subskrybuj zmiany wartości dla nowo dodanej kontrolki
        this.form
          .get(controlName)
          ?.valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.hasUnsavedChanges = true;
            this.canPrint = false;
          });
      }
    });
  }

  /**
   * Funkcja odpowiedzialna za aktualizację wartości kontrolek tłumaczeń drugorzędnych.
   * @since 13.07.2026
   */
  private patchSecondaryTranslationControls(values: Record<string, any>): void {
    const patch: Record<string, any> = {};

    this.requiredTranslationVariables.forEach((variable) => {
      const valueKey = this.toControlName(variable.key);
      const translationControl = this.toRequiredTranslationControlName(
        variable.key,
      );
      patch[translationControl] = values?.[valueKey] ?? "";
    });

    this.form.patchValue(patch, { emitEvent: false });
  }

  /**
   * Funkcja odpowiedzialna za ładowanie tłumaczeń drugorzędnych z zapisanych wartości pól.
   * @since 13.07.2026
   */
  private loadRequiredTranslationsFromFieldValues(
    language: string,
    fieldValues: Record<string, any>,
  ): void {
    if (!language || !fieldValues) {
      return;
    }

    if (!this.requiredTranslationsByLanguage[language]) {
      this.requiredTranslationsByLanguage[language] = {};
    }

    // Załaduj tłumaczenia dla języków drugorzędnych z meta-klucza
    const storedMeta = fieldValues[this.REQUIRED_TRANSLATIONS_META_KEY];
    if (storedMeta && typeof storedMeta === "object") {
      // storedMeta zawiera { "EN": {...}, "ES": {...} } itp.
      Object.keys(storedMeta).forEach((secondaryLang) => {
        this.requiredTranslationsByLanguage[language][secondaryLang] = {
          ...(this.requiredTranslationsByLanguage[language][secondaryLang] ||
            {}),
          ...storedMeta[secondaryLang],
        };
      });
    }
  }

  /**
   * Funkcja odpowiedzialna za zwrócenie klucza wybranego języka drugorzędnego.
   * @since 13.07.2026
   */
  private getSelectedSecondaryLanguageKey(): string {
    return this.form.get("secondaryLanguage")?.value || "";
  }

  /**
   * Funkcja odpowiedzialna za budowanie wartości zmiennych dla języka drugorzędnego.
   * @since 13.07.2026
   */
  private buildSecondaryLanguageValues(
    primaryValues: Record<string, any>,
  ): Record<string, any> {
    const primaryLanguage = this.getPrimaryLanguageKey();
    const secondaryLanguage = this.getSelectedSecondaryLanguageKey();
    const secondaryValues: Record<string, any> = { ...primaryValues };

    if (
      !primaryLanguage ||
      !secondaryLanguage ||
      secondaryLanguage === primaryLanguage
    ) {
      return secondaryValues;
    }

    const translationValues =
      this.requiredTranslationsByLanguage[primaryLanguage]?.[
        secondaryLanguage
      ] || {};
    this.requiredTranslationVariables.forEach((variable) => {
      const baseControl = this.toControlName(variable.key);
      const translationControl = this.toRequiredTranslationControlName(
        variable.key,
      );
      const translatedValue =
        translationValues[baseControl] ?? translationValues[translationControl];

      if (
        translatedValue !== undefined &&
        translatedValue !== null &&
        translatedValue !== ""
      ) {
        secondaryValues[baseControl] = translatedValue;
      }
    });

    return secondaryValues;
  }

  /**
   * Funkcja odpowiedzialna za zwrócenie klucza głównego języka.
   * @since 13.07.2026
   */
  private getPrimaryLanguageKey(): string {
    return this.form.get("language")?.value || this.activeLanguage || "";
  }

  /**
   * Funkcja odpowiedzialna za blokowanie zmiany wartości pól number przy przewijaniu kółkiem myszy.
   * @since 20.04.2026
   */
  preventNumberScroll(event: WheelEvent): void {
    event.preventDefault();
    (event.target as HTMLInputElement)?.blur();
  }

  /**
   * Funkcja odpowiedzialna za resetowanie zapisanego szablonu dokumentu.
   * Czyści renderedContent, aby zastosować nową konstrukcję szablonu.
   * Dostępna tylko dla superadmin.
   * @since 27.04.2026
   */
  resetDocumentTemplate(): void {
    if (this.userRole !== "superadmin" || !this.orderId) {
      return;
    }

    const language = this.form.get("language")?.value;
    if (!language) {
      return;
    }

    // Importuj DialogComponent z shared
    import("app/shared/dialog/dialog.component").then(({ DialogComponent }) => {
      const dialogRef = this.dialog.open(DialogComponent, {
        width: "400px",
        data: {
          text: `Czy na pewno chcesz zresetować szablon umowy?`,
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result?.status === true) {
          this.isResetting = true;
          this.documentDetailsService
            .resetDocumentDetailsTemplate({
              orderId: this.orderId || "",
              type: DocumentType.SALES_AGREEMENT,
            })
            .subscribe({
              next: () => {
                this.isResetting = false;
                // Wymuś nowy render: pobierz świeży szablon i przygotuj do zapisania
                this.documentService
                  .getSingleDocument({
                    language,
                    type: DocumentType.SALES_AGREEMENT,
                  })
                  .subscribe({
                    next: (agreement: Document) => {
                      this.currentAgreement = agreement;
                      const rendered = this.renderTemplate(agreement.content);
                      this.rawRenderedHtml = rendered;
                      // Odśwież podgląd
                      this.refreshPreview();
                    },
                    error: (err: HttpErrorResponse) => {
                      console.error(
                        "Błąd pobierania świeżego szablonu po resecie:",
                        err,
                      );
                    },
                  });
              },
              error: (error: HttpErrorResponse) => {
                console.error("Błąd resetowania szablonu dokumentu:", error);
                this.isResetting = false;
              },
            });
        }
      });
    });
  }

  /**
   * Funkcja odpowiedzialna za jawny zapis wersji językowej dokumentu umowy sprzedaży.
   * W trybie dwujęzycznym zapisuje obie wersje językowe.
   * @since 17.04.2026
   */
  saveDocument(): void {
    const language = this.form.get("language")?.value;
    const includeDualLanguage = !!this.form.get("includeDualLanguage")?.value;
    const secondaryLanguage = this.form.get("secondaryLanguage")?.value;

    if (!this.orderId || !language) {
      return;
    }

    // Jeśli tryb dwujęzyczny jest włączony, musimy pobrać i zapisać oba szablony
    if (
      includeDualLanguage &&
      secondaryLanguage &&
      secondaryLanguage !== language
    ) {
      this.saveDualLanguageDocument(language, secondaryLanguage);
      return;
    }

    // Tryb jednojęzyczny - standardowe zapisywanie
    if (this.currentAgreement && this.rawRenderedHtml !== null) {
      this.saveDocumentRecord(this.currentAgreement, this.rawRenderedHtml);
      return;
    }

    // Brak podglądu – pobierz szablon, wyrenderuj i zapisz
    this.isSaving = true;

    this.documentService
      .getSingleDocument({ language, type: DocumentType.SALES_AGREEMENT })
      .subscribe({
        next: (agreement: Document) => {
          const rendered = this.renderTemplate(agreement.content);
          this.rawRenderedHtml = rendered;
          this.currentAgreement = agreement;
          this.saveDocumentRecord(agreement, rendered);
          this.isSaving = false;
        },
        error: (err: HttpErrorResponse) => {
          console.error("Błąd pobierania szablonu przy zapisie:", err);
          this.isSaving = false;
        },
      });
  }

  /**
   * Funkcja odpowiedzialna za zapis dokumentu w trybie dwujęzycznym.
   * Pobiera szablony dla obu języków, renderuje je i zapisuje w tablicy templates.
   * @since 13.07.2026
   */
  private saveDualLanguageDocument(
    primaryLanguage: string,
    secondaryLanguage: string,
  ): void {
    if (!this.orderId) {
      return;
    }

    this.isSaving = true;
    this.persistSecondaryTranslationDraft();

    const primaryValues = this.form.getRawValue();
    const secondaryValues = this.buildSecondaryLanguageValues(primaryValues);

    // Pobierz szablony dla obu języków równolegle
    forkJoin({
      primaryDoc: this.documentService.getSingleDocument({
        language: primaryLanguage,
        type: DocumentType.SALES_AGREEMENT,
      }),
      secondaryDoc: this.documentService.getSingleDocument({
        language: secondaryLanguage,
        type: DocumentType.SALES_AGREEMENT,
      }),
    }).subscribe({
      next: ({ primaryDoc, secondaryDoc }) => {
        // Renderuj oba dokumenty
        const primaryRendered = this.renderTemplate(
          primaryDoc.content,
          false,
          primaryValues,
        );
        const secondaryRendered = this.renderTemplate(
          secondaryDoc.content,
          false,
          secondaryValues,
        );

        // Użyj documentId z głównego dokumentu (oba powinny mieć ten sam szablon bazowy)
        const documentId = primaryDoc._id || secondaryDoc._id;
        if (!documentId) {
          console.error("Brak documentId w szablonach");
          this.isSaving = false;
          return;
        }

        // Zapisz oba szablony
        this.saveDualDocumentRecord(
          documentId,
          primaryLanguage,
          primaryDoc.content,
          primaryRendered,
          secondaryLanguage,
          secondaryDoc.content,
          secondaryRendered,
        );
      },
      error: (err: HttpErrorResponse) => {
        console.error(
          "Błąd pobierania szablonów przy zapisie dwujęzycznym:",
          err,
        );
        this.isSaving = false;
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za zapis rekordu z dwoma wersjami językowymi.
   * @since 13.07.2026
   */
  private saveDualDocumentRecord(
    documentId: string,
    primaryLanguage: string,
    primaryTemplate: string,
    primaryRendered: string,
    secondaryLanguage: string,
    secondaryTemplate: string,
    secondaryRendered: string,
  ): void {
    if (!this.orderId) {
      return;
    }

    // Budujemy tablicę templates z oboma językami
    const templates: DocumentTemplate[] = [
      {
        language: primaryLanguage,
        documentTemplate: primaryTemplate,
        document: primaryRendered,
      },
      {
        language: secondaryLanguage,
        documentTemplate: secondaryTemplate,
        document: secondaryRendered,
      },
    ];

    const payload: DocumentDetailsRecord = {
      orderId: this.orderId,
      documentId,
      type: DocumentType.SALES_AGREEMENT,
      fieldValues: this.getCurrentVariableValues(),
      flexibleFields: SALES_AGREEMENT_VARIABLES.filter((v) => v.flexible).map(
        (v) => this.toControlName(v.key),
      ),
      templates,
    };

    this.documentDetailsService.upsertDocumentDetails(payload).subscribe({
      next: () => {
        // Zaktualizuj dane w pamięci dla głównego języka
        this.draftValuesByLanguage[primaryLanguage] = {
          ...payload.fieldValues,
        };
        this.persistedOrderDerivedValuesByLanguage[primaryLanguage] =
          this.pickOrderDerivedFieldValues(payload.fieldValues || {});

        // Ustaw currentAgreement na główny język
        this.currentAgreement = {
          _id: documentId,
          language: primaryLanguage,
          type: DocumentType.SALES_AGREEMENT,
          content: primaryTemplate,
        };
        this.rawRenderedHtml = primaryRendered;

        this.hasUnsavedChanges = false;
        this.canPrint = true;
        this.isSaving = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error("Błąd zapisu dokumentu dwujęzycznego:", error);
        this.isSaving = false;
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za zapis rekordu dokumentu umowy (wersja językowa).
   * Zapisuje wartości pól formularza, identyfikator szablonu i wyrenderowaną treść.
   * @since 17.04.2026
   */
  private saveDocumentRecord(
    agreement: Document,
    renderedContent: string,
  ): void {
    const language = this.form.get("language")?.value;
    if (!this.orderId || !agreement?._id || !language) {
      return;
    }
    this.isSaving = true;

    // Budujemy tablicę templates z szablonem dla aktualnego języka
    const template: DocumentTemplate = {
      language,
      documentTemplate: agreement.content,
      document: renderedContent,
    };

    const payload: DocumentDetailsRecord = {
      orderId: this.orderId,
      documentId: agreement._id,
      type: DocumentType.SALES_AGREEMENT,
      fieldValues: this.getCurrentVariableValues(),
      flexibleFields: SALES_AGREEMENT_VARIABLES.filter((v) => v.flexible).map(
        (v) => this.toControlName(v.key),
      ),
      templates: [template],
    };

    this.documentDetailsService.upsertDocumentDetails(payload).subscribe({
      next: () => {
        this.draftValuesByLanguage[language] = { ...payload.fieldValues };
        this.persistedOrderDerivedValuesByLanguage[language] =
          this.pickOrderDerivedFieldValues(payload.fieldValues || {});
        this.hasUnsavedChanges = false;
        this.canPrint = true;
        this.isSaving = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error("Błąd zapisu dokumentu umowy:", error);
        this.isSaving = false;
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za pobranie aktualnych wartości pól zmiennych z formularza.
   * @since 17.04.2026
   */
  private getCurrentVariableValues(): Record<string, any> {
    const values: Record<string, any> = {};
    const language = this.getPrimaryLanguageKey();
    const persistedOrderDerived = language
      ? this.persistedOrderDerivedValuesByLanguage[language] || {}
      : {};

    SALES_AGREEMENT_VARIABLES.forEach((variable) => {
      const controlName = this.toControlName(variable.key);

      if (variable.orderDerived && !this.canEditVariable(variable)) {
        const hasPersistedValue = Object.prototype.hasOwnProperty.call(
          persistedOrderDerived,
          controlName,
        );

        if (hasPersistedValue) {
          values[controlName] = persistedOrderDerived[controlName];
        }
        return;
      }

      if (variable.orderDerived && this.canEditVariable(variable)) {
        const hasPersistedValue = Object.prototype.hasOwnProperty.call(
          persistedOrderDerived,
          controlName,
        );
        const wasEdited = !!this.form.get(controlName)?.dirty;

        if (!hasPersistedValue && !wasEdited) {
          return;
        }
      }

      values[controlName] = this.form.get(controlName)?.value ?? "";
    });

    // Zbierz tłumaczenia z formularza (dynamiczne kontrolki)
    const primaryLanguage = this.activeLanguage;
    const cleanedTranslations: Record<string, Record<string, any>> = {};

    if (primaryLanguage) {
      this.requiredTranslationVariables.forEach((variable) => {
        const valueKey = this.toControlName(variable.key);
        const controlName = this.toRequiredTranslationControlName(variable.key);
        const control = this.form.get(controlName);
        if (control) {
          // Mamy aktywną kontrolkę tłumaczenia - zbierz ją
          const secondaryLanguage = this.activeSecondaryLanguage;
          if (secondaryLanguage) {
            if (!cleanedTranslations[secondaryLanguage]) {
              cleanedTranslations[secondaryLanguage] = {};
            }
            cleanedTranslations[secondaryLanguage][valueKey] =
              control.value ?? "";
          }
        }
      });
    }

    // Dodaj pozostałe tłumaczenia z pamięci (z innych języków drugorzędnych)
    if (
      primaryLanguage &&
      this.requiredTranslationsByLanguage[primaryLanguage]
    ) {
      Object.keys(this.requiredTranslationsByLanguage[primaryLanguage]).forEach(
        (secondaryLang) => {
          if (!cleanedTranslations[secondaryLang]) {
            cleanedTranslations[secondaryLang] =
              this.requiredTranslationsByLanguage[primaryLanguage][
                secondaryLang
              ];
          }
        },
      );
    }

    values[this.REQUIRED_TRANSLATIONS_META_KEY] = cleanedTranslations;

    return values;
  }

  /**
   * Zwraca tylko pola oznaczone jako orderDerived.
   */
  private pickOrderDerivedFieldValues(
    fieldValues: Record<string, any>,
  ): Record<string, any> {
    if (!fieldValues || typeof fieldValues !== "object") {
      return {};
    }

    const derivedControlNames = new Set(
      SALES_AGREEMENT_VARIABLES.filter((variable) => variable.orderDerived).map(
        (variable) => this.toControlName(variable.key),
      ),
    );

    const picked: Record<string, any> = {};
    Object.keys(fieldValues).forEach((key) => {
      if (!derivedControlNames.has(key)) {
        return;
      }
      picked[key] = fieldValues[key];
    });

    return picked;
  }

  /**
   * Funkcja odpowiedzialna za zastępowanie wszystkich wystąpień zmiennych szablonu ({{klucz}})
   * wartościami z formularza. Formatuje daty i kwoty na wartości czytelne dla użytkownika.
   * @since 17.04.2026
   * @param template Surowy HTML szablonu z placeholderami zmiennych
   * @param highlightVariables Gdy true, wartości zmiennych są owijane spanem z czerwonym kolorem
   */
  private renderTemplate(
    template: string,
    highlightVariables = false,
    valuesOverride?: Record<string, any>,
  ): string {
    let result = template;
    const sourceValues = valuesOverride || this.form.getRawValue();

    // Kompatybilność ze starym i nowym placeholderem podpisu
    // ({{signature}}, {{podpis}}, także z opcjonalnymi spacjami).
    const signatureImgTag = `<img src="${this.SIGNATURE_IMAGE_PATH}" alt="Podpis" class="signature-image" style="max-width:220px;max-height:120px;" />`;
    result = result.replace(/{{\s*(podpis|signature)\s*}}/gi, signatureImgTag);

    SALES_AGREEMENT_VARIABLES.forEach((v) => {
      const controlName = this.toControlName(v.key);

      // Pole podpisu: zawsze zastępujemy grafiką podpisu
      if (v.disabled && v.key === "{{podpis}}") {
        result = result.split(v.key).join(signatureImgTag);
        return;
      }

      let value: string = sourceValues[controlName] ?? "";

      if (v.formatter === "date" && value) {
        value = this.datePipe.transform(value, "dd.MM.yyyy") || value;
      } else if (v.renderAsCurrency && value !== "") {
        const currency =
          sourceValues["currency"] ||
          this.order?.finances?.prices?.currencyChoice ||
          "EUR";
        value = this.formatCurrency(value, currency);
      } else {
        value = String(value);
      }
      // Globalne zastąpienie wszystkich wystąpień klucza
      const displayValue = highlightVariables
        ? `<span style="color:red;font-weight:700;">${value}</span>`
        : value;
      result = result.split(v.key).join(displayValue);
    });
    return result;
  }

  /**
   * Funkcja odpowiedzialna za wyświetlenie podglądu dokumentu w iframe lub jego ukrycie.
   * @since 20.04.2026
   */
  previewDocument(): void {
    // Toggle: jeśli podgląd jest widoczny – ukryj go
    if (this.isPreviewVisible) {
      this.isPreviewVisible = false;
      this.previewHtml = null;
      return;
    }

    const language = this.form.get("language")?.value;
    if (!language) {
      return;
    }
    if (this.currentAgreement) {
      this.refreshPreview();
      this.isPreviewVisible = true;
    } else {
      this.documentService
        .getSingleDocument({ language, type: DocumentType.SALES_AGREEMENT })
        .subscribe({
          next: (agreement: Document) => {
            this.currentAgreement = agreement;
            this.refreshPreview();
            this.isPreviewVisible = true;
          },
          error: (err: HttpErrorResponse) => {
            console.error("Błąd pobierania szablonu przy podglądzie:", err);
          },
        });
    }
  }

  /**
   * Funkcja odpowiedzialna za odświeżenie HTML podglądu na podstawie aktualnych wartości formularza.
   * @since 20.04.2026
   */
  private refreshPreview(): void {
    if (!this.currentAgreement) return;
    const rendered = this.renderTemplate(this.currentAgreement.content, true);
    const fullHtml = this.buildPreviewHtml(rendered);
    this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(fullHtml);
  }

  /**
   * Funkcja odpowiedzialna za czyszczenie zasobów komponentu.
   * @since 20.04.2026
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Funkcja odpowiedzialna za budowanie pełnego dokumentu HTML do wyświetlenia w iframe.
   * @since 20.04.2026
   */
  private buildPreviewHtml(content: string): string {
    return `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <style>
    body { margin: 0; padding: 24px 32px; box-sizing: border-box; }
    p { margin: 0 0 6px; line-height: 1.5; }
    h1, h2, h3 { margin: 12px 0 6px; }
    table { border-collapse: collapse; width: 100%; }
    td, th { border: 1px solid #ccc; padding: 4px 8px; }
  </style>
</head>
<body>${content}</body>
</html>`;
  }

  /**
   * Funkcja odpowiedzialna za otwarcie okna druku z wyrenderowaną treścią dokumentu.

   * @since 17.04.2026
   */
  printDocument(): void {
    const language = this.form.get("language")?.value;
    if (!language) {
      return;
    }

    const includeDualLanguage = !!this.form.get("includeDualLanguage")?.value;
    const secondaryLanguage = this.form.get("secondaryLanguage")?.value;

    if (includeDualLanguage) {
      if (!secondaryLanguage || secondaryLanguage === language) {
        return;
      }
      this.printDualLanguageDocument(language, secondaryLanguage);
      return;
    }

    const currentValues = this.form.getRawValue();
    this.getRenderedHtmlForLanguage(language, true, currentValues).subscribe({
      next: (rendered: string) => {
        this.rawRenderedHtml = rendered;
        this.openPrintWindow(rendered);
      },
      error: (err: HttpErrorResponse) => {
        console.error("Błąd pobierania treści dokumentu przy druku:", err);
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za druk dokumentu dwujęzycznego w układzie 50/50.
   * @since 17.04.2026
   */
  private printDualLanguageDocument(
    primaryLanguage: string,
    secondaryLanguage: string,
  ): void {
    this.persistSecondaryTranslationDraft();
    const primaryValues = this.form.getRawValue();
    const secondaryValues = this.buildSecondaryLanguageValues(primaryValues);
    forkJoin({
      primaryHtml: this.getRenderedHtmlForLanguage(
        primaryLanguage,
        true,
        primaryValues,
      ),
      secondaryHtml: this.getRenderedHtmlForLanguage(
        secondaryLanguage,
        false,
        secondaryValues,
      ),
    }).subscribe({
      next: ({ primaryHtml, secondaryHtml }) => {
        this.rawRenderedHtml = primaryHtml;
        this.openPrintWindow(primaryHtml, secondaryHtml);
      },
      error: (err: HttpErrorResponse) => {
        console.error("Błąd przygotowania dokumentu dwujęzycznego:", err);
      },
    });
  }

  /**
   * Funkcja odpowiedzialna za zwracanie wyrenderowanego HTML dokumentu dla zadanego języka.
   * Priorytet: cache aktywnego języka -> zapisany rekord -> render z szablonu.
   */
  private getRenderedHtmlForLanguage(
    language: string,
    allowCurrentCache: boolean,
    valuesOverride?: Record<string, any>,
  ): Observable<string> {
    const currentLanguage = this.form.get("language")?.value;
    if (
      allowCurrentCache &&
      currentLanguage === language &&
      this.rawRenderedHtml
    ) {
      return of(this.rawRenderedHtml);
    }

    if (!this.orderId) {
      return this.renderHtmlFromTemplate(language);
    }
    return this.documentDetailsService
      .getSingleDocumentDetails({
        orderId: this.orderId,
        type: DocumentType.SALES_AGREEMENT,
      })
      .pipe(
        map((record: DocumentDetailsRecord) => {
          const template = getTemplateForLanguage(record?.templates, language);
          return template?.documentTemplate || "";
        }),
        switchMap((savedTemplate: string) => {
          if (savedTemplate) {
            // Zawsze renderuj z szablonu używając aktualnych wartości z formularza
            return of(
              this.renderTemplate(
                savedTemplate,
                false,
                valuesOverride || this.form.getRawValue(),
              ),
            );
          }
          return this.renderHtmlFromTemplate(language, valuesOverride);
        }),
        catchError(() => this.renderHtmlFromTemplate(language, valuesOverride)),
      );
  }

  /**
   * Funkcja odpowiedzialna za renderowanie HTML na podstawie szablonu dla wskazanego języka
   * z opcjonalnym nadpisaniem wartości zmiennych.
   */
  private renderHtmlFromTemplate(
    language: string,
    valuesOverride?: Record<string, any>,
  ): Observable<string> {
    return this.documentService
      .getSingleDocument({
        language,
        type: DocumentType.SALES_AGREEMENT,
      })
      .pipe(
        map((agreement: Document) =>
          this.renderTemplate(agreement.content, false, valuesOverride),
        ),
      );
  }

  /**
   * Otwiera nowe okno przeglądarki z dokumentem gotowym do wydruku / zapisania jako PDF.
   * Gdy podano secondaryHtml, treść jest dzielona na dwa równe kolumny (layout dwujęzyczny).
   * Header i footer są przylepione do góry/dołu każdej strony (position: fixed).
   * Tytuł dokumentu pochodzi z numeru VIN zamówienia.
   * @since 17.04.2026
   */
  private openPrintWindow(primaryHtml: string, secondaryHtml?: string): void {
    const isDual = !!secondaryHtml;
    const title = this.order?.vinNumber
      ? `Umowa sprzedaży – ${this.order.vinNumber}`
      : "Umowa sprzedaży";
    const headerHeightMm = this.PRINT_HEADER_HEIGHT_MM;
    const headerLogoMaxHeightMm = this.PRINT_HEADER_LOGO_MAX_HEIGHT_MM;
    const footerHeightMm = this.PRINT_FOOTER_HEIGHT_MM;
    const footerMaxLines = this.PRINT_FOOTER_MAX_LINES;
    const safeGapMm = this.PRINT_CONTENT_SAFE_GAP_MM;

    const headerLogoBlock = this.PRINT_HEADER_IMAGE_PATH
      ? `<div class="print-header-logo"><img src="${this.PRINT_HEADER_IMAGE_PATH}" alt="Logo" /></div>`
      : "";
    const headerContactBlock = this.PRINT_HEADER_CONTACT_HTML
      ? `<div class="print-header-contact">${this.PRINT_HEADER_CONTACT_HTML}</div>`
      : "";
    const headerBlock =
      headerLogoBlock || headerContactBlock
        ? `<div class="print-header"><div class="print-header-inner">${headerLogoBlock}${headerContactBlock}</div></div>`
        : "";
    const footerBlock = this.PRINT_FOOTER_HTML
      ? `<div class="print-footer"><div class="print-footer-inner"><div class="print-footer-content">${this.PRINT_FOOTER_HTML}</div></div></div>`
      : "";

    const bodyContent = isDual
      ? `<div class="dual-body">
    <div class="dual-col">${primaryHtml}</div>
    <div class="dual-col">${secondaryHtml}</div>
  </div>`
      : `<div class="document-body">${primaryHtml}</div>`;

    const sideMargin = isDual ? "14mm" : "18mm";

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${title}</title>
  <style>
    @page { size: A4; margin: 10mm ${sideMargin}mm; }
    * { box-sizing: border-box; }
    html, body {
      width: 100%;
      height: 100%;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 10.5pt;
      line-height: 1.5;
      color: #111;
      margin: 0;
    }

    /* Układ strony: nagłówek i stopka jako grupy tabeli, dzięki temu nie nakładają się na treść */
    .print-layout {
      width: 100%;
      height: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .print-layout thead { display: table-header-group; }
    .print-layout tfoot { display: table-footer-group; }

    .print-header {
      height: calc(${headerHeightMm}mm + ${safeGapMm}mm);
      background: #fff;
      overflow: hidden;
    }
    .print-header-inner {
      height: ${headerHeightMm}mm;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6mm;
      background: #fff;
      border-bottom: 1px solid #e0e0e0;
      padding: 0;
    }
    .print-header-logo {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      min-width: 0;
    }
    .print-header-logo img {
      max-height: ${headerLogoMaxHeightMm}mm;
      width: auto;
      display: block;
      margin: 0;
    }
    .print-header-contact {
      flex: 1 1 auto;
      min-width: 0;
      text-align: right;
      font-size: 7.3pt;
      line-height: 1.22;
      color: #444;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .print-header-contact p {
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .print-footer {
      height: calc(${footerHeightMm}mm + ${safeGapMm}mm);
      display: flex;
      align-items: flex-end;
      background: #fff;
      overflow: hidden;
    }
    .print-footer-inner {
      width: 100%;
      height: ${footerHeightMm}mm;
      border-top: 1px solid #ccc;
      padding: 2mm 0 1.5mm 0;
      font-size: 7pt;
      color: #555;
      background: #fff;
    }
    .print-footer-content {
      max-height: calc(${footerHeightMm}mm - 4mm);
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: ${footerMaxLines};
    }
    .print-footer p { margin: 1px 0; }

    .print-content { vertical-align: top; }

    /* Treść */
    .document-body table,
    .dual-col table { width: 100%; border-collapse: collapse; }
    .document-body td, .document-body th,
    .dual-col td, .dual-col th { border: 1px solid #ccc; padding: 4px 8px; }

    /* Tabela podpisów: całość na jednej stronie + stała wysokość pól podpisu */
    .signatures {
      width: 100%;
      page-break-inside: avoid;
      break-inside: avoid;
      margin-top: 6px;
    }
    .signatures tr,
    .signatures td,
    .signatures tbody {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .signatures .signature-area {
      height: 120px;
      min-height: 120px;
      vertical-align: top;
    }

    .signature-image { max-width: 220px; max-height: 120px; display: block; margin-top: 8px; }
    /* Dwujęzyczny układ kolumn */
    .dual-body { width: 100%; display: flex; align-items: flex-start; gap: 10mm; }
    .dual-col { width: calc(50% - 5mm); flex: 0 0 calc(50% - 5mm); min-width: 0; }
    @media print { .no-print { display: none; } }
  </style>
</head>
<body>
  <table class="print-layout" role="presentation">
    <thead>
      <tr>
        <td>${headerBlock}</td>
      </tr>
    </thead>
    <tfoot>
      <tr>
        <td>${footerBlock}</td>
      </tr>
    </tfoot>
    <tbody>
      <tr>
        <td class="print-content">${bodyContent}</td>
      </tr>
    </tbody>
  </table>
  <script>
    window.onload = function() { window.print(); };
  <\/script>
</body>
</html>`;

    const printWindow = window.open(
      "",
      "_blank",
      isDual ? "width=1200,height=900" : "width=900,height=700",
    );
    if (!printWindow) {
      console.warn(
        "Przeglądarka zablokowała otwarcie nowego okna. Zezwól na pop-upy dla tej strony.",
      );
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }
}
